'use client';

import {
  type PropsWithChildren,
  type FC,
  useEffect,
  cloneElement,
  ReactElement,
  ReactNode,
} from 'react';
import { ChevronLeft, FilterOutline } from '@eqi-investimentos/icons/web';
import { ButtonProps } from '@eqi-investimentos/react';
import {
  Drawer as ChakraDrawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  type CloseButtonProps,
  type DrawerContentProps,
  type DrawerProps as ChakraDrawerProps,
  type ModalFooterProps,
  type ModalHeaderProps,
  type HTMLChakraProps,
} from '@chakra-ui/react';
import type { IconButtonProps } from '@/components/elements/IconButton';
import { IconButton } from '@/components/elements/IconButton';
import { IfRender } from '@/utils/jsx';

export const Drawer: FC<PropsWithChildren<DrawerProps>> = ({
  children,
  appendChildren,
  backButtonProps,
  bodyProps = { py: '0', px: '3.25rem' },
  closeButtonProps,
  contentProps = { position: 'relative' },
  customButton,
  drawerProps,
  footerContent,
  footerProps,
  headerContent,
  headerProps,
  iconButton,
  id,
  showCloseButton = true,
  showCustomButton = false,
  showIconButton = true,
  showHeaderBackButton = true,
  customBackButtonAction = null,
  handleBeforeDrawerClose,
  handleBeforeDrawerOpen,
  handleAfterDrawerOpen,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleOpen = () => {
    if (handleBeforeDrawerOpen) {
      handleBeforeDrawerOpen();
    }
    onOpen();
    if (handleAfterDrawerOpen) {
      handleAfterDrawerOpen();
    }
  };

  const handleClose = () => {
    if (handleBeforeDrawerClose) {
      handleBeforeDrawerClose();
    }
    onClose();
  };

  useEffect(() => {
    if (id) {
      window.addEventListener(`close-chakra-modal-${id}`, handleClose);
      window.addEventListener(`open-chakra-modal-${id}`, handleOpen);

      setTimeout(() => {
        window.dispatchEvent(new Event(`loaded-chakra-modal-${id}`));
      }, 500);
    }

    return () => {
      if (id) {
        window.removeEventListener(`close-chakra-modal-${id}`, handleClose);
        window.removeEventListener(`open-chakra-modal-${id}`, handleOpen);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.dispatchEvent(new Event('recheck-monetary-visibility'));
  }, [isOpen]);

  return (
    <>
      <IfRender condition={showIconButton}>
        <IconButton
          ariaLabel="Abrir menu"
          onButtonClick={handleOpen}
          {...iconButton}
          icon={iconButton?.icon || FilterOutline}
        />
      </IfRender>
      <IfRender condition={showCustomButton}>
        {!!customButton && cloneElement(customButton, { onClick: handleOpen })}
      </IfRender>
      <ChakraDrawer
        id={id}
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        {...drawerProps}
      >
        <DrawerOverlay />
        <DrawerContent
          maxW="28.375rem"
          bg="background.primary"
          position="relative"
          {...contentProps}
        >
          <IfRender condition={showCloseButton}>
            <DrawerCloseButton
              top="4xl"
              right="3.25rem"
              h="fit-content"
              w="fit-content"
              color="content.primary"
              {...closeButtonProps}
              sx={{ svg: { w: '1rem', h: '1rem' } }}
            />
          </IfRender>
          <IfRender condition={!!appendChildren}>{appendChildren}</IfRender>
          <IfRender condition={typeof headerContent === 'object'}>
            <DrawerHeader py="3xl" pl="3xl" pr="s" {...headerProps}>
              <IfRender condition={showHeaderBackButton}>
                <IconButton
                  ariaLabel="Fechar menu"
                  icon={ChevronLeft}
                  onButtonClick={customBackButtonAction || handleClose}
                  buttonProps={{
                    pos: 'absolute',
                    top: '4xl',
                    left: 5,
                    ...backButtonProps?.buttonProps,
                  }}
                  iconProps={{
                    w: '2rem !important',
                    h: '2rem !important',
                    color: 'content.primary',
                    ...backButtonProps?.iconProps,
                  }}
                />
              </IfRender>
              {headerContent}
            </DrawerHeader>
          </IfRender>
          <DrawerBody {...bodyProps}>{children}</DrawerBody>
          <IfRender condition={typeof footerContent === 'object'}>
            <DrawerFooter
              display="flex"
              px="2.25rem"
              pb="1.625rem"
              pt="0"
              {...footerProps}
            >
              {footerContent}
            </DrawerFooter>
          </IfRender>
        </DrawerContent>
      </ChakraDrawer>
    </>
  );
};

export type DrawerProps = {
  appendChildren?: ReactNode;
  backButtonProps?: Omit<
    IconButtonProps,
    'children' | 'ariaLabel' | 'icon' | 'onButtonClick'
  >;
  bodyProps?: HTMLChakraProps<'div'>;
  closeButtonProps?: CloseButtonProps;
  contentProps?: DrawerContentProps;
  customButton?: ReactElement<ButtonProps>;
  drawerProps?: Omit<
    ChakraDrawerProps,
    'children' | 'isOpen' | 'onClose' | 'finalFocusRef'
  >;
  footerContent?: ReactNode;
  footerProps?: ModalFooterProps;
  headerContent?: ReactNode;
  headerProps?: ModalHeaderProps;
  iconButton?: Omit<IconButtonProps, 'ref' | 'onButtonClick' | 'ariaLabel'>;
  id?: string;
  showCloseButton?: boolean;
  showCustomButton?: boolean;
  showHeaderBackButton?: boolean;
  showIconButton?: boolean;
  customBackButtonAction?: () => void;
  handleBeforeDrawerClose?: () => void;
  handleBeforeDrawerOpen?: () => void;
  handleAfterDrawerOpen?: () => void;
};
